import type { BranchTree, ProjectArrivedChange, ProjectMergeConflict } from '../services/projectService'
import type { CodeItemGroup, CodeRow } from '../types/codeIndex'
import { normalizeAppendixRefs, normalizeGroupAppendixRefs } from './appendixRefs'

export type CodeSheetDiffStatus = 'added' | 'modified' | 'deleted' | 'unchanged'

export type CodeSheetDiffField =
  | 'codeItem'
  | 'position'
  | 'title'
  | 'attributeSchemas'
  | 'authoring'
  | 'subgroups'
  | 'optionCode'
  | 'description'
  | 'availability'
  | 'status'
  | 'attributes'
  | 'regions'
  | 'rowGroup'
  | 'order'
  | 'valuePosition'
  | 'appendixRefs'
  | 'groupAppendixRefs'

export interface FieldDiff<T = unknown> {
  before: T | null
  after: T | null
}

export interface LogicalRowDiff {
  key: string
  kind: 'group' | 'row'
  status: CodeSheetDiffStatus
  domainId: string | null
  groupKey: string
  projectGroupIndex: number | null
  projectRowIndex: number | null
  liveGroupIndex: number | null
  liveRowIndex: number | null
  projectGroup: CodeItemGroup | null
  projectRow: CodeRow | null
  liveGroup: CodeItemGroup | null
  liveRow: CodeRow | null
  fields: Partial<Record<CodeSheetDiffField, FieldDiff>>
  arrivedStatus?: CodeSheetDiffStatus
  arrivedFields?: Partial<Record<CodeSheetDiffField, FieldDiff>>
  arrivedRevision?: string | null
  conflictFields?: CodeSheetDiffField[]
  searchableText: string
}

export interface CodeSheetDiffCounts {
  added: number
  modified: number
  deleted: number
  arrived: number
}

export interface CodeSheetDiffModel {
  entries: LogicalRowDiff[]
  counts: CodeSheetDiffCounts
  groupByProjectIndex: Map<number, LogicalRowDiff>
  rowByProjectPosition: Map<string, LogicalRowDiff>
  deletedRowsByProjectGroupIndex: Map<number, LogicalRowDiff[]>
  deletedGroups: LogicalRowDiff[]
  arrivedRowsByProjectGroupIndex: Map<number, LogicalRowDiff[]>
  arrivedGroups: LogicalRowDiff[]
  entryKeyByTargetDomainId: Map<string, string>
  entryByKey: Map<string, LogicalRowDiff>
}

type DomainLookup = Map<string, string>

const TECHNICAL_KEYS = new Set([
  'id',
  'rowId',
  'localKey',
  'isNew',
  'descriptionBlockId',
  'availabilityBlockId',
  'createdAt',
  'updatedAt',
  'created_at',
  'updated_at',
  'deleted_at',
])

function buildDomainLookup(tree: BranchTree | null | undefined): DomainLookup {
  return new Map(
    (tree?.nodes || [])
      .filter((node): node is typeof node & { id: string } => Boolean(node.id))
      .map((node) => [node.elementId, String(node.id)])
  )
}

function domainIdFor(value: { id?: string | null } | null | undefined, lookup: DomainLookup): string | null {
  const elementId = String(value?.id || '').trim()
  return elementId ? lookup.get(elementId) || null : null
}

function localGroupKey(group: CodeItemGroup, index: number, side: 'project' | 'live'): string {
  return `${side}:group:${group.localKey || group.id || `${group.codeItemPos.code}:${index}`}`
}

function localRowKey(row: CodeRow, groupKey: string, index: number, side: 'project' | 'live'): string {
  return `${side}:row:${groupKey}:${row.rowId || row.id || index}`
}

function normalizeStatus(value: string | null | undefined): string {
  const normalized = String(value ?? '').trim()
  return normalized.toLowerCase() === 'empty' ? '' : normalized
}

function normalizeSemanticValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeSemanticValue)
  if (!value || typeof value !== 'object') return value ?? null

  return Object.keys(value as Record<string, unknown>)
    .filter((key) => !TECHNICAL_KEYS.has(key))
    .sort()
    .reduce<Record<string, unknown>>((result, key) => {
      result[key] = normalizeSemanticValue((value as Record<string, unknown>)[key])
      return result
    }, {})
}

function valuesEqual(before: unknown, after: unknown): boolean {
  return JSON.stringify(normalizeSemanticValue(before)) === JSON.stringify(normalizeSemanticValue(after))
}

function addField(
  fields: Partial<Record<CodeSheetDiffField, FieldDiff>>,
  field: CodeSheetDiffField,
  before: unknown,
  after: unknown
) {
  if (!valuesEqual(before, after)) {
    fields[field] = { before: before ?? null, after: after ?? null }
  }
}

function groupFields(live: CodeItemGroup | null, project: CodeItemGroup | null) {
  const fields: Partial<Record<CodeSheetDiffField, FieldDiff>> = {}
  addField(fields, 'codeItem', live?.codeItemPos.code ?? null, project?.codeItemPos.code ?? null)
  addField(fields, 'position', live?.codeItemPos.posRange ?? null, project?.codeItemPos.posRange ?? null)
  addField(fields, 'title', live?.title ?? '', project?.title ?? '')
  addField(fields, 'attributeSchemas', live?.attributeSchemas ?? [], project?.attributeSchemas ?? [])
  addField(fields, 'authoring', live?.authoringMode ?? 'simple', project?.authoringMode ?? 'simple')
  addField(fields, 'subgroups', live?.subgroups ?? [], project?.subgroups ?? [])
  addField(fields, 'appendixRefs', normalizeAppendixRefs(live?.appendixRefs), normalizeAppendixRefs(project?.appendixRefs))
  addField(
    fields,
    'groupAppendixRefs',
    normalizeGroupAppendixRefs(live?.groupAppendixRefs),
    normalizeGroupAppendixRefs(project?.groupAppendixRefs)
  )
  return fields
}

function isBlockLeader(rows: CodeRow[], index: number, field: 'description' | 'availability'): boolean {
  const key = field === 'description' ? 'descriptionBlockId' : 'availabilityBlockId'
  const blockId = rows[index]?.[key]
  if (!blockId) return true
  return rows.findIndex((row) => row[key] === blockId) === index
}

function rowFields(
  liveGroup: CodeItemGroup | null,
  liveRow: CodeRow | null,
  liveRowIndex: number | null,
  projectGroup: CodeItemGroup | null,
  projectRow: CodeRow | null,
  projectRowIndex: number | null
) {
  const fields: Partial<Record<CodeSheetDiffField, FieldDiff>> = {}
  addField(fields, 'optionCode', liveRow?.optionCode ?? null, projectRow?.optionCode ?? null)
  addField(fields, 'status', normalizeStatus(liveRow?.status), normalizeStatus(projectRow?.status))
  addField(fields, 'attributes', liveRow?.attributeValues ?? {}, projectRow?.attributeValues ?? {})
  addField(fields, 'regions', liveRow?.regions ?? [], projectRow?.regions ?? [])
  addField(fields, 'appendixRefs', normalizeAppendixRefs(liveRow?.appendixRefs), normalizeAppendixRefs(projectRow?.appendixRefs))
  addField(fields, 'rowGroup', liveRow?.group ?? null, projectRow?.group ?? null)
  addField(fields, 'order', liveRow ? liveRow.position + 1 : null, projectRow ? projectRow.position + 1 : null)
  addField(
    fields,
    'valuePosition',
    [liveRow?.valuePosition ?? null, liveRow?.valuePositionEnd ?? null],
    [projectRow?.valuePosition ?? null, projectRow?.valuePositionEnd ?? null]
  )

  const liveDescriptionLeader = liveGroup && liveRowIndex !== null
    ? isBlockLeader(liveGroup.rows, liveRowIndex, 'description')
    : false
  const projectDescriptionLeader = projectGroup && projectRowIndex !== null
    ? isBlockLeader(projectGroup.rows, projectRowIndex, 'description')
    : false
  if (liveDescriptionLeader || projectDescriptionLeader || !liveRow || !projectRow) {
    addField(fields, 'description', liveRow?.description ?? null, projectRow?.description ?? null)
  }

  const liveAvailabilityLeader = liveGroup && liveRowIndex !== null
    ? isBlockLeader(liveGroup.rows, liveRowIndex, 'availability')
    : false
  const projectAvailabilityLeader = projectGroup && projectRowIndex !== null
    ? isBlockLeader(projectGroup.rows, projectRowIndex, 'availability')
    : false
  if (liveAvailabilityLeader || projectAvailabilityLeader || !liveRow || !projectRow) {
    addField(fields, 'availability', liveRow?.rule ?? null, projectRow?.rule ?? null)
  }

  return fields
}

function statusFor(liveValue: unknown, projectValue: unknown, fields: object): CodeSheetDiffStatus {
  if (liveValue == null) return 'added'
  if (projectValue == null) return 'deleted'
  return Object.keys(fields).length ? 'modified' : 'unchanged'
}

function toSearchableText(...values: unknown[]): string {
  return values
    .map((value) => {
      if (typeof value === 'string') return value
      try {
        return JSON.stringify(normalizeSemanticValue(value))
      } catch {
        return String(value ?? '')
      }
    })
    .join(' ')
    .toLowerCase()
}

function buildOwnerDomainLookup(tree: BranchTree | null | undefined): Map<string, string> {
  if (!tree) return new Map()
  const nodesByElementId = new Map(tree.nodes.map((node) => [node.elementId, node]))
  const parentByChild = new Map(tree.edges.map((edge) => [edge.childElementId, edge.parentElementId]))
  const result = new Map<string, string>()

  tree.nodes.forEach((node) => {
    if (!node.id) return
    let current = node
    const visited = new Set<string>()
    while (current && !visited.has(current.elementId)) {
      visited.add(current.elementId)
      if ((current.nodeType === 'FormatValue' || current.nodeType === 'FormatCode') && current.id) {
        result.set(String(node.id), String(current.id))
        return
      }
      const parentId = parentByChild.get(current.elementId)
      if (!parentId) return
      const parent = nodesByElementId.get(parentId)
      if (!parent) return
      current = parent
    }
  })

  return result
}

function buildTwoWayCodeSheetDiffModel({
  liveGroups,
  projectGroups,
  liveTree,
  projectTree,
}: {
  liveGroups: CodeItemGroup[]
  projectGroups: CodeItemGroup[]
  liveTree: BranchTree | null
  projectTree: BranchTree | null
}): CodeSheetDiffModel {
  const liveLookup = buildDomainLookup(liveTree)
  const projectLookup = buildDomainLookup(projectTree)
  const liveGroupsByDomain = new Map<string, { group: CodeItemGroup; index: number }>()
  const projectGroupsByDomain = new Map<string, { group: CodeItemGroup; index: number }>()

  liveGroups.forEach((group, index) => {
    const domainId = domainIdFor(group, liveLookup)
    if (domainId) liveGroupsByDomain.set(domainId, { group, index })
  })
  projectGroups.forEach((group, index) => {
    const domainId = domainIdFor(group, projectLookup)
    if (domainId) projectGroupsByDomain.set(domainId, { group, index })
  })

  const groupDomainIds = [
    ...Array.from(liveGroupsByDomain.keys()),
    ...Array.from(projectGroupsByDomain.keys()).filter((id) => !liveGroupsByDomain.has(id)),
  ]
  const groupPairs: Array<{
    domainId: string | null
    live: { group: CodeItemGroup; index: number } | null
    project: { group: CodeItemGroup; index: number } | null
  }> = groupDomainIds.map((domainId) => ({
    domainId,
    live: liveGroupsByDomain.get(domainId) || null,
    project: projectGroupsByDomain.get(domainId) || null,
  }))

  projectGroups.forEach((group, index) => {
    if (domainIdFor(group, projectLookup)) return
    groupPairs.push({ domainId: null, live: null, project: { group, index } })
  })
  groupPairs.sort((left, right) => {
    const leftOrder = left.project?.index ?? left.live?.index ?? Number.MAX_SAFE_INTEGER
    const rightOrder = right.project?.index ?? right.live?.index ?? Number.MAX_SAFE_INTEGER
    return leftOrder - rightOrder
  })

  const entries: LogicalRowDiff[] = []

  groupPairs.forEach(({ domainId, live, project }) => {
    const liveGroup = live?.group || null
    const projectGroup = project?.group || null
    const groupKey = domainId || (projectGroup
      ? localGroupKey(projectGroup, project!.index, 'project')
      : localGroupKey(liveGroup!, live!.index, 'live'))
    const fields = groupFields(liveGroup, projectGroup)
    const groupEntry: LogicalRowDiff = {
      key: `group:${groupKey}`,
      kind: 'group',
      status: statusFor(liveGroup, projectGroup, fields),
      domainId,
      groupKey,
      projectGroupIndex: project?.index ?? null,
      projectRowIndex: null,
      liveGroupIndex: live?.index ?? null,
      liveRowIndex: null,
      projectGroup,
      projectRow: null,
      liveGroup,
      liveRow: null,
      fields,
      searchableText: toSearchableText(liveGroup, projectGroup),
    }
    entries.push(groupEntry)

    const liveRowsByDomain = new Map<string, { row: CodeRow; index: number }>()
    const projectRowsByDomain = new Map<string, { row: CodeRow; index: number }>()
    liveGroup?.rows.forEach((row, index) => {
      const rowDomainId = domainIdFor(row, liveLookup)
      if (rowDomainId) liveRowsByDomain.set(rowDomainId, { row, index })
    })
    projectGroup?.rows.forEach((row, index) => {
      const rowDomainId = domainIdFor(row, projectLookup)
      if (rowDomainId) projectRowsByDomain.set(rowDomainId, { row, index })
    })

    const rowDomainIds = [
      ...Array.from(liveRowsByDomain.keys()),
      ...Array.from(projectRowsByDomain.keys()).filter((id) => !liveRowsByDomain.has(id)),
    ]
    const rowPairs: Array<{
      domainId: string | null
      live: { row: CodeRow; index: number } | null
      project: { row: CodeRow; index: number } | null
    }> = rowDomainIds.map((rowDomainId) => ({
      domainId: rowDomainId,
      live: liveRowsByDomain.get(rowDomainId) || null,
      project: projectRowsByDomain.get(rowDomainId) || null,
    }))

    projectGroup?.rows.forEach((row, index) => {
      if (domainIdFor(row, projectLookup)) return
      rowPairs.push({ domainId: null, live: null, project: { row, index } })
    })

    rowPairs
      .sort((left, right) => {
        const leftOrder = left.project?.index ?? left.live?.index ?? Number.MAX_SAFE_INTEGER
        const rightOrder = right.project?.index ?? right.live?.index ?? Number.MAX_SAFE_INTEGER
        return leftOrder - rightOrder
      })
      .forEach((pair) => {
        const liveRow = pair.live?.row || null
        const projectRow = pair.project?.row || null
        const rowKey = pair.domainId || (projectRow
          ? localRowKey(projectRow, groupKey, pair.project!.index, 'project')
          : localRowKey(liveRow!, groupKey, pair.live!.index, 'live'))
        const rowDiffFields = rowFields(
          liveGroup,
          liveRow,
          pair.live?.index ?? null,
          projectGroup,
          projectRow,
          pair.project?.index ?? null
        )
        entries.push({
          key: `row:${rowKey}`,
          kind: 'row',
          status: statusFor(liveRow, projectRow, rowDiffFields),
          domainId: pair.domainId,
          groupKey,
          projectGroupIndex: project?.index ?? null,
          projectRowIndex: pair.project?.index ?? null,
          liveGroupIndex: live?.index ?? null,
          liveRowIndex: pair.live?.index ?? null,
          projectGroup,
          projectRow,
          liveGroup,
          liveRow,
          fields: rowDiffFields,
          searchableText: toSearchableText(liveGroup?.codeItemPos, projectGroup?.codeItemPos, liveRow, projectRow),
        })
      })
  })

  const counts = entries.reduce<CodeSheetDiffCounts>((result, entry) => {
    if (entry.status !== 'unchanged') result[entry.status] += 1
    return result
  }, { added: 0, modified: 0, deleted: 0, arrived: 0 })

  const groupByProjectIndex = new Map<number, LogicalRowDiff>()
  const rowByProjectPosition = new Map<string, LogicalRowDiff>()
  const deletedRowsByProjectGroupIndex = new Map<number, LogicalRowDiff[]>()
  const deletedGroups: LogicalRowDiff[] = []
  const entryByKey = new Map(entries.map((entry) => [entry.key, entry]))
  const logicalEntryByDomainId = new Map(
    entries
      .filter((entry): entry is LogicalRowDiff & { domainId: string } => Boolean(entry.domainId))
      .map((entry) => [entry.domainId, entry])
  )

  entries.forEach((entry) => {
    if (entry.kind === 'group' && entry.projectGroupIndex !== null) {
      groupByProjectIndex.set(entry.projectGroupIndex, entry)
    }
    if (entry.kind === 'row' && entry.projectGroupIndex !== null && entry.projectRowIndex !== null) {
      rowByProjectPosition.set(`${entry.projectGroupIndex}:${entry.projectRowIndex}`, entry)
    }
    if (entry.status === 'deleted' && entry.kind === 'row') {
      const projectGroupEntry = entries.find((candidate) => (
        candidate.kind === 'group' && candidate.groupKey === entry.groupKey && candidate.projectGroupIndex !== null
      ))
      if (projectGroupEntry?.projectGroupIndex !== null && projectGroupEntry?.projectGroupIndex !== undefined) {
        const current = deletedRowsByProjectGroupIndex.get(projectGroupEntry.projectGroupIndex) || []
        current.push(entry)
        deletedRowsByProjectGroupIndex.set(projectGroupEntry.projectGroupIndex, current)
      }
    }
    if (entry.status === 'deleted' && entry.kind === 'group') deletedGroups.push(entry)
  })

  const entryKeyByTargetDomainId = new Map<string, string>()
  const ownerLookups = [buildOwnerDomainLookup(liveTree), buildOwnerDomainLookup(projectTree)]
  ownerLookups.forEach((owners) => {
    owners.forEach((ownerDomainId, targetDomainId) => {
      const owner = logicalEntryByDomainId.get(ownerDomainId)
      if (owner) entryKeyByTargetDomainId.set(targetDomainId, owner.key)
    })
  })
  logicalEntryByDomainId.forEach((entry, domainId) => entryKeyByTargetDomainId.set(domainId, entry.key))

  deletedRowsByProjectGroupIndex.forEach((rows) => rows.sort((a, b) => (
    (a.liveRowIndex ?? Number.MAX_SAFE_INTEGER) - (b.liveRowIndex ?? Number.MAX_SAFE_INTEGER)
  )))

  return {
    entries,
    counts,
    groupByProjectIndex,
    rowByProjectPosition,
    deletedRowsByProjectGroupIndex,
    deletedGroups,
    arrivedRowsByProjectGroupIndex: new Map(),
    arrivedGroups: [],
    entryKeyByTargetDomainId,
    entryByKey,
  }
}

export function buildCodeSheetDiffModel({
  liveGroups,
  projectGroups,
  liveTree,
  projectTree,
  baseGroups,
  baseTree,
  arrivedChanges = [],
  authorBaseGroups,
  authorBaseTree,
  conflicts,
}: {
  liveGroups: CodeItemGroup[]
  projectGroups: CodeItemGroup[]
  liveTree: BranchTree | null
  projectTree: BranchTree | null
  baseGroups?: CodeItemGroup[]
  baseTree?: BranchTree | null
  arrivedChanges?: ProjectArrivedChange[]
  authorBaseGroups?: CodeItemGroup[]
  authorBaseTree?: BranchTree | null
  conflicts?: ProjectMergeConflict[]
}): CodeSheetDiffModel {
  if (!baseGroups || !baseTree) {
    return buildTwoWayCodeSheetDiffModel({ liveGroups, projectGroups, liveTree, projectTree })
  }

  const author = buildTwoWayCodeSheetDiffModel({
    liveGroups: authorBaseGroups || baseGroups,
    projectGroups,
    liveTree: authorBaseTree || baseTree,
    projectTree,
  })
  const arrived = buildTwoWayCodeSheetDiffModel({
    liveGroups: baseGroups,
    projectGroups: liveGroups,
    liveTree: baseTree,
    projectTree: liveTree,
  })
  const keyFor = (entry: LogicalRowDiff) => `${entry.kind}:${entry.domainId || entry.key}`
  const arrivedByLogicalKey = new Map(arrived.entries.map(entry => [keyFor(entry), entry]))
  const authorKeys = new Set(author.entries.map(keyFor))
  const projectGroupIndexByDomainId = new Map(
    author.entries
      .filter((entry): entry is LogicalRowDiff & { domainId: string; projectGroupIndex: number } => (
        entry.kind === 'group' && Boolean(entry.domainId) && entry.projectGroupIndex !== null
      ))
      .map(entry => [entry.domainId, entry.projectGroupIndex])
  )

  const ownerLookup = buildOwnerDomainLookup(baseTree)
  buildOwnerDomainLookup(liveTree).forEach((ownerId, nodeId) => ownerLookup.set(nodeId, ownerId))
  const semanticConflictFields = (entry: LogicalRowDiff): CodeSheetDiffField[] => {
    const result = new Set<CodeSheetDiffField>()
    for (const conflict of conflicts || []) {
      if ((ownerLookup.get(conflict.nodeId) || conflict.nodeId) !== entry.domainId) continue
      if (!conflict.path) {
        Object.keys(entry.fields).forEach(field => result.add(field as CodeSheetDiffField))
      } else if (conflict.nodeType && !['FormatCode', 'FormatValue'].includes(conflict.nodeType)) {
        result.add('availability')
      } else {
        const fields: Record<string, CodeSheetDiffField> = {
          name: entry.kind === 'group' ? 'codeItem' : 'optionCode', value: 'optionCode',
          code_item: 'codeItem', position: entry.kind === 'group' ? 'position' : 'order', len: 'position',
          title: 'title', desc: entry.kind === 'group' ? 'title' : 'description', description: 'description',
          status: 'status', group: 'rowGroup', regions: 'regions', attribute_values: 'attributes',
          attribute_schemas: 'attributeSchemas', value_position: 'valuePosition', value_position_end: 'valuePosition',
          appendix_refs: 'appendixRefs', group_appendix_refs: 'groupAppendixRefs',
        }
        if (fields[conflict.path]) result.add(fields[conflict.path])
      }
    }
    return Array.from(result)
  }
  const provenanceByOwner = new Map<string, ProjectArrivedChange[]>()
  arrivedChanges.forEach(change => {
    const ownerId = ownerLookup.get(change.nodeId) || change.nodeId
    const current = provenanceByOwner.get(ownerId) || []
    current.push(change)
    provenanceByOwner.set(ownerId, current)
  })
  const revisionFor = (entry: LogicalRowDiff): string | null => {
    if (!entry.domainId) return null
    const sources = provenanceByOwner.get(entry.domainId) || []
    const sorted = [...sources]
      .sort((left, right) => String(left.mergedAt || '').localeCompare(String(right.mergedAt || '')))
    return sorted.length > 0 ? sorted[sorted.length - 1].revision || null : null
  }

  const combined = author.entries.map(entry => {
    const arrivedEntry = arrivedByLogicalKey.get(keyFor(entry))
    const hasArrived = Boolean(arrivedEntry && arrivedEntry.status !== 'unchanged')
    const authorFields = Object.keys(entry.fields) as CodeSheetDiffField[]
    const arrivedFields = Object.keys(arrivedEntry?.fields || {}) as CodeSheetDiffField[]
    const conflictFields = conflicts !== undefined ? semanticConflictFields(entry) : entry.status !== 'unchanged' && hasArrived
      ? authorFields.filter(field => (
          arrivedFields.includes(field)
          && !valuesEqual(entry.fields[field]?.after, arrivedEntry?.fields[field]?.after)
        ))
      : []
    return {
      ...entry,
      arrivedStatus: hasArrived ? arrivedEntry!.status : 'unchanged',
      arrivedFields: hasArrived ? arrivedEntry!.fields : {},
      arrivedRevision: hasArrived ? revisionFor(arrivedEntry!) : null,
      conflictFields,
      liveGroup: arrivedEntry?.projectGroup || entry.liveGroup,
      liveRow: arrivedEntry?.projectRow || entry.liveRow,
    }
  })
  arrived.entries
    .filter(entry => entry.status !== 'unchanged' && !authorKeys.has(keyFor(entry)))
    .forEach(entry => {
      const projectGroupIndex = entry.kind === 'row'
        ? projectGroupIndexByDomainId.get(entry.groupKey) ?? null
        : null
      combined.push({
        ...entry,
        status: 'unchanged',
        fields: {},
        arrivedStatus: entry.status,
        arrivedFields: entry.fields,
        arrivedRevision: revisionFor(entry),
        conflictFields: [],
        projectGroupIndex,
        projectRowIndex: null,
        liveGroup: entry.projectGroup,
        liveRow: entry.projectRow,
      })
    })

  const arrivedCount = combined.filter(entry => entry.arrivedStatus && entry.arrivedStatus !== 'unchanged').length
  const entryByKey = new Map(combined.map(entry => [entry.key, entry]))
  const entryKeyByTargetDomainId = new Map(author.entryKeyByTargetDomainId)
  arrived.entryKeyByTargetDomainId.forEach((value, key) => entryKeyByTargetDomainId.set(key, value))
  const groupByProjectIndex = new Map<number, LogicalRowDiff>()
  const rowByProjectPosition = new Map<string, LogicalRowDiff>()
  const deletedRowsByProjectGroupIndex = new Map<number, LogicalRowDiff[]>()
  const deletedGroups: LogicalRowDiff[] = []
  const arrivedRowsByProjectGroupIndex = new Map<number, LogicalRowDiff[]>()
  const arrivedGroups: LogicalRowDiff[] = []
  combined.forEach(entry => {
    if (entry.kind === 'group' && entry.projectGroupIndex !== null) {
      groupByProjectIndex.set(entry.projectGroupIndex, entry)
    }
    if (entry.kind === 'row' && entry.projectGroupIndex !== null && entry.projectRowIndex !== null) {
      rowByProjectPosition.set(`${entry.projectGroupIndex}:${entry.projectRowIndex}`, entry)
    }
    if (entry.status === 'deleted' && entry.kind === 'row' && entry.projectGroupIndex !== null) {
      const current = deletedRowsByProjectGroupIndex.get(entry.projectGroupIndex) || []
      current.push(entry)
      deletedRowsByProjectGroupIndex.set(entry.projectGroupIndex, current)
    }
    if (entry.status === 'deleted' && entry.kind === 'group') deletedGroups.push(entry)
    if (entry.status === 'unchanged' && entry.arrivedStatus === 'added' && entry.kind === 'row' && entry.projectGroupIndex !== null) {
      const current = arrivedRowsByProjectGroupIndex.get(entry.projectGroupIndex) || []
      current.push(entry)
      arrivedRowsByProjectGroupIndex.set(entry.projectGroupIndex, current)
    }
    if (entry.status === 'unchanged' && entry.arrivedStatus === 'added' && entry.kind === 'group') {
      arrivedGroups.push(entry)
    }
  })

  arrivedRowsByProjectGroupIndex.forEach(rows => rows.sort((left, right) => (
    (left.liveRowIndex ?? Number.MAX_SAFE_INTEGER) - (right.liveRowIndex ?? Number.MAX_SAFE_INTEGER)
  )))
  arrivedGroups.sort((left, right) => (
    (left.liveGroupIndex ?? Number.MAX_SAFE_INTEGER) - (right.liveGroupIndex ?? Number.MAX_SAFE_INTEGER)
  ))

  return {
    ...author,
    entries: combined,
    counts: { ...author.counts, arrived: arrivedCount },
    groupByProjectIndex,
    rowByProjectPosition,
    deletedRowsByProjectGroupIndex,
    deletedGroups,
    arrivedRowsByProjectGroupIndex,
    arrivedGroups,
    entryByKey,
    entryKeyByTargetDomainId,
  }
}

export function formatDiffValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Empty'
  if (Array.isArray(value)) return value.map(formatDiffValue).join(' – ')
  if (typeof value === 'object') {
    const normalized = normalizeSemanticValue(value)
    if (normalized && typeof normalized === 'object' && 'description' in normalized) {
      const description = (normalized as Record<string, unknown>).description
      if (description) return String(description)
    }
    return JSON.stringify(normalized)
  }
  return String(value)
}
